import { type CSSProperties } from 'react';
import ReactSelectBase, { components } from 'react-select';

const ReactSelect = ReactSelectBase as any;

export interface SelectOption {
  label?: string;
  value?: number | string;
}

interface SelectProps<O extends SelectOption, V> {
  autoFocus?: boolean;
  className?: string;
  clearValue?: string | null;
  closeMenuOnSelect?: boolean;
  defaultMenuIsOpen?: boolean;
  disabled?: boolean;
  filterOption?: (option: O, input: string) => boolean;
  inputId?: string | null;
  isClearable?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  onChange: (value: V, action: string, option?: O) => void;
  options: readonly O[];
  overflowContainer?: boolean;
  placeholder?: string | null;
  selected?: O | O[] | null;
  testId?: string;
}

const Select = <O extends SelectOption, V>({
  autoFocus = false,
  className = '',
  clearValue = null,
  closeMenuOnSelect = true,
  defaultMenuIsOpen = false,
  disabled = false,
  filterOption,
  inputId = null,
  isClearable = true,
  isMulti = false,
  isSearchable = true,
  onChange,
  options,
  overflowContainer = false,
  placeholder = null,
  selected = null,
  testId,
}: SelectProps<O, V>) => {
  const onSelect = (selectedOptions: O | O[] | null, { action, option }: { action: string; option?: O }) => {
    switch (action) {
      case 'clear':
        onChange(clearValue as V, action, option);
        break;
      case 'pop-value':
      case 'remove-value':
      case 'select-option':
        onChange(selectedOptions as V, action, option);
        break;
      default:
        break;
    }
  };

  return (
    <ReactSelect
      autoFocus={autoFocus}
      className={`react-select-container ${className}`}
      clearIndicatorTestId={testId ? `${testId}-clear-indicator` : null}
      closeMenuOnSelect={closeMenuOnSelect}
      components={{ SelectContainer, Menu, ClearIndicator }}
      defaultMenuIsOpen={defaultMenuIsOpen}
      filterOption={filterOption}
      inputId={inputId}
      isClearable={isClearable}
      isDisabled={disabled}
      isMulti={isMulti}
      isSearchable={isSearchable}
      menuPortalTarget={overflowContainer ? document.body : null}
      menuTestId={testId ? `${testId}-menu` : null}
      onChange={onSelect}
      options={options}
      placeholder={placeholder}
      styles={{ menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 1100 }) }}
      testId={testId}
      value={selected}
    />
  );
};

export default Select;

// Extend components to help with testing
// For details, see https://react-select.com/components#replacing-components
const SelectContainer = ({ innerProps, ...props }: any) => (
  <components.SelectContainer
    {...props}
    innerProps={{
      ...innerProps,
      'data-testid': props.selectProps.testId,
    }}
  >
    {props.children}
  </components.SelectContainer>
);

const Menu = ({ innerProps, ...props }: any) => (
  <components.Menu
    {...props}
    innerProps={{
      ...innerProps,
      'data-testid': props.selectProps.menuTestId,
    }}
  >
    {props.children}
  </components.Menu>
);

const ClearIndicator = ({ innerProps, ...props }: any) => (
  <components.ClearIndicator
    {...props}
    innerProps={{
      ...innerProps,
      'data-testid': props.selectProps.clearIndicatorTestId,
    }}
  >
    {props.children}
  </components.ClearIndicator>
);
